import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo, useRef } from 'react'
import useSWR, { mutate as globalMutate } from 'swr'
import useSWRInfinite from 'swr/infinite'

export enum NotificationStatus {
  UNREAD = 'unread',
  READ = 'read',
  ARCHIVED = 'archived',
}

export interface Notification {
  id: string
  message: string
  title?: string
  body?: string
  subtitle?: string
  status: NotificationStatus
  created_at: string
  delivered_at?: string
  isUnread: boolean
  cta?: {
    label: string
    url: string
  }
}

export interface ListNotificationsResponse {
  notifications: Notification[]
  total: number
  page: number
  page_size: number
}

type ServerNotificationItem = {
  id: string
  notification: {
    status: NotificationStatus
    created_at: string
    delivered_at?: string | null
    payload: {
      title?: string | null
      body?: string | null
      subtitle?: string | null
      cta?: { label: string; url: string } | null
    }
  }
  rendered?: { inapp_rich_text?: string | null; html_email?: string | null }
}

type ServerListNotificationsResponse = {
  items: ServerNotificationItem[]
  page: number
  page_size: number
  total: number | null
}

interface UseNotificationsOptions {
  status?: NotificationStatus
  pageSize?: number
  enabled?: boolean
  unreadPageSize?: number
  unreadMaxPages?: number
}

const mapServerToClient = (
  server: ServerListNotificationsResponse
): ListNotificationsResponse => {
  const notifications: Notification[] = (server.items ?? []).map(
    ({ id, notification, rendered }) => {
      const fallback =
        (notification.payload?.title ?? '').trim() +
        (notification.payload?.body ? `\n\n${notification.payload.body}` : '')
      return {
        id,
        message: (rendered?.inapp_rich_text ?? fallback).trim(),
        title: notification.payload?.title ?? undefined,
        body: notification.payload?.body ?? undefined,
        subtitle: notification.payload?.subtitle ?? undefined,
        status: notification.status,
        created_at: notification.created_at,
        delivered_at: notification.delivered_at ?? undefined,
        isUnread: notification.status === NotificationStatus.UNREAD,
        cta: notification.payload?.cta ?? undefined,
      }
    }
  )
  return {
    notifications,
    total: server.total ?? notifications.length,
    page: server.page,
    page_size: server.page_size,
  }
}

const unreadKeyFor = (id: string) => `unread:${id}`

export const useNotifications = (options: UseNotificationsOptions = {}) => {
  const { ALERTS_URL } = useRuntimeVariables()
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { user } = useGeckoPermissions()

  const {
    status,
    pageSize = 25,
    enabled = true,
    unreadPageSize = 100,
    unreadMaxPages = 10,
  } = options

  const effectivePageSize = Math.min(pageSize, 100)

  const buildUrl = useCallback(
    (q: { status?: NotificationStatus; page: number; pageSize: number }) => {
      const params = new URLSearchParams()
      if (user?.id) params.append('user_id', user.id)
      if (q.status) params.append('status', q.status)
      params.append('page', String(q.page))
      params.append('page_size', String(Math.min(q.pageSize, 100)))
      return `${ALERTS_URL}/api/v1/notifications?${params.toString()}`
    },
    [ALERTS_URL, user?.id]
  )

  // ── Paginated list via useSWRInfinite ───────────────────────────────

  const getKey = useCallback(
    (pageIndex: number, previousPageData: ListNotificationsResponse | null) => {
      if (!enabled || !isAuthenticated || !user?.id) return null
      if (
        previousPageData &&
        previousPageData.notifications.length < effectivePageSize
      ) {
        return null
      }
      return buildUrl({
        status,
        page: pageIndex + 1,
        pageSize: effectivePageSize,
      })
    },
    [enabled, isAuthenticated, user?.id, buildUrl, status, effectivePageSize]
  )

  const fetcher = useCallback(
    async (url: string): Promise<ListNotificationsResponse> => {
      if (!isAuthenticated || !user?.id)
        throw new Error('User is not authenticated')
      const res = await axios.get<ServerListNotificationsResponse>(url, {
        headers: { Accept: 'application/json' },
      })
      return mapServerToClient(res.data)
    },
    [axios, isAuthenticated, user?.id]
  )

  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<ListNotificationsResponse, Error>(getKey, fetcher, {
      revalidateFirstPage: true,
      revalidateAll: false,
    })

  const notifications = useMemo(
    () =>
      data
        ? data
            .flatMap((page) => page.notifications)
            .filter((n) => !status || n.status === status)
        : [],
    [data, status]
  )

  const total = data?.[0]?.total ?? 0

  const isLoadingMore =
    size > 1 && data !== undefined && typeof data[size - 1] === 'undefined'

  const canLoadMore =
    !!data &&
    data.length > 0 &&
    (data[data.length - 1]?.notifications.length ?? 0) >= effectivePageSize

  const loadMore = useCallback(() => {
    if (canLoadMore && !isLoadingMore) {
      setSize((s) => s + 1)
    }
  }, [canLoadMore, isLoadingMore, setSize])

  // ── Unread count ────────────────────────────────────────────────────

  const unreadKey = useMemo(
    () =>
      enabled && isAuthenticated && user?.id ? unreadKeyFor(user.id) : null,
    [enabled, isAuthenticated, user?.id]
  )

  const collectUnread = useCallback(async (): Promise<Notification[]> => {
    if (!isAuthenticated || !user?.id) return []
    const all: Notification[] = []
    for (let p = 1; p <= unreadMaxPages; p++) {
      const url = buildUrl({
        status: NotificationStatus.UNREAD,
        page: p,
        pageSize: unreadPageSize,
      })
      const res = await axios.get<ServerListNotificationsResponse>(url, {
        headers: { Accept: 'application/json' },
      })
      const mapped = mapServerToClient(res.data)
      all.push(...mapped.notifications)
      if (mapped.notifications.length < Math.min(unreadPageSize, 100)) break
    }
    return all
  }, [
    axios,
    buildUrl,
    isAuthenticated,
    user?.id,
    unreadMaxPages,
    unreadPageSize,
  ])

  const fetchUnreadCount = useCallback(async (): Promise<number> => {
    if (!isAuthenticated || !user?.id)
      throw new Error('User is not authenticated')
    return (await collectUnread()).length
  }, [collectUnread, isAuthenticated, user?.id])

  const {
    data: unreadCountData,
    error: unreadError,
    isLoading: isLoadingUnread,
    isValidating: isValidatingUnread,
    mutate: mutateUnread,
  } = useSWR<number, Error>(unreadKey, fetchUnreadCount, {
    revalidateOnFocus: true,
    refreshInterval: 30_000,
  })

  const unreadCount = unreadCountData ?? 0

  // ── Refs for stable callback identity ───────────────────────────────

  const dataRef = useRef(data)
  dataRef.current = data
  const unreadRef = useRef(unreadCountData)
  unreadRef.current = unreadCountData

  // ── Mutations ───────────────────────────────────────────────────────

  const patchBulkRead = useCallback(
    async (ids: string[]) => {
      await axios.patch(
        `${ALERTS_URL}/api/v1/notifications/read`,
        { notification_ids: ids },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      )
    },
    [ALERTS_URL, axios]
  )

  const markRead = useCallback(
    async (notificationIds: string[]) => {
      if (!isAuthenticated || !user?.id || notificationIds.length === 0) return

      const previousData = dataRef.current
      const previousUnreadCount = unreadRef.current

      if (previousData) {
        mutate(
          previousData.map((page) => ({
            ...page,
            notifications: page.notifications.map((n) =>
              notificationIds.includes(n.id)
                ? { ...n, status: NotificationStatus.READ, isUnread: false }
                : n
            ),
          })),
          false
        )
      }
      if (unreadRef.current !== undefined) {
        mutateUnread(0, false)
      }

      try {
        await patchBulkRead(notificationIds)
        await Promise.all([mutate(), mutateUnread()])
      } catch (error) {
        if (previousData) mutate(previousData, false)
        if (previousUnreadCount !== undefined)
          mutateUnread(previousUnreadCount, false)
        throw error
      }
    },
    [patchBulkRead, isAuthenticated, user?.id, mutate, mutateUnread]
  )

  const markReadWhere = useCallback(
    async (predicate: (n: Notification) => boolean) => {
      if (!isAuthenticated || !user?.id) return

      const unread = await collectUnread()
      const matchIds = unread
        .filter((n) => n.isUnread && predicate(n))
        .map((n) => n.id)
      if (matchIds.length === 0) return

      await patchBulkRead(matchIds)
      // Refresh every unread-count cache (incl. the bell's), independent of this
      // instance's enabled state; dropdown list self-revalidates on next open.
      await globalMutate(unreadKeyFor(user.id))
    },
    [collectUnread, patchBulkRead, isAuthenticated, user?.id]
  )

  const updateNotificationStatus = useCallback(
    async (
      notificationId: string,
      newStatus: NotificationStatus,
      snoozedUntil?: string
    ) => {
      if (!isAuthenticated || !user?.id) {
        throw new Error('User is not authenticated')
      }

      const previousData = dataRef.current
      const previousUnreadCount = unreadRef.current

      if (previousData) {
        mutate(
          previousData.map((page) => ({
            ...page,
            notifications: page.notifications.map((n) =>
              n.id === notificationId
                ? {
                    ...n,
                    status: newStatus,
                    isUnread: newStatus === NotificationStatus.UNREAD,
                  }
                : n
            ),
          })),
          false
        )
      }

      if (unreadRef.current !== undefined) {
        const currentUnread = unreadRef.current
        const newCount =
          newStatus === NotificationStatus.READ ||
          newStatus === NotificationStatus.ARCHIVED
            ? Math.max(0, currentUnread - 1)
            : newStatus === NotificationStatus.UNREAD
              ? currentUnread + 1
              : currentUnread
        mutateUnread(newCount, false)
      }

      try {
        await axios.patch(
          `${ALERTS_URL}/api/v1/notifications/${notificationId}/status`,
          {
            status: newStatus,
            ...(snoozedUntil ? { snoozed_until: snoozedUntil } : {}),
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          }
        )

        await Promise.all([mutate(), mutateUnread()])
      } catch (error) {
        if (previousData) mutate(previousData, false)
        if (previousUnreadCount !== undefined)
          mutateUnread(previousUnreadCount, false)
        throw error
      }
    },
    [ALERTS_URL, axios, isAuthenticated, user?.id, mutate, mutateUnread]
  )

  return {
    notifications,
    total,

    isLoadingMore,
    canLoadMore,
    loadMore,

    unreadCount,
    isLoadingUnread,
    isValidatingUnread,
    unreadError,

    isLoading,
    isValidating,
    error,

    mutate,
    mutateUnread,
    updateNotificationStatus,
    markRead,
    markReadWhere,
  }
}
